import { FONT_FAMILIES, type ThemeRadius, type ThemeTokens } from '../api/shopPresets';

const RADIUS_PX: Record<ThemeRadius, string> = {
  sharp: '0',
  rounded: '0.5rem',
  soft: '1rem',
};

/** All CSS custom properties written by applyThemeTokens. */
const THEME_PROPS = [
  '--bulma-primary-h',
  '--bulma-primary-s',
  '--bulma-primary-l',
  '--bulma-link-h',
  '--bulma-link-s',
  '--bulma-link-l',
  '--brand-primary',
  '--brand-accent',
  '--bulma-radius',
  '--brand-radius',
  '--bulma-family-primary',
  '--brand-font',
] as const;

/** Convert a #rrggbb string to integer HSL components. */
export function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const m = hex.replace('#', '');
  const r = parseInt(m.slice(0, 2), 16) / 255;
  const g = parseInt(m.slice(2, 4), 16) / 255;
  const b = parseInt(m.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r: h = (((g - b) / d) % 6 + 6) % 6; break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h *= 60;
  }
  return { h: Math.round(h), s: Math.round(s * 100), l: Math.round(l * 100) };
}

function setScheme(target: HTMLElement, name: 'primary' | 'link', hex: string): void {
  const { h, s, l } = hexToHsl(hex);
  target.style.setProperty(`--bulma-${name}-h`, `${h}deg`);
  target.style.setProperty(`--bulma-${name}-s`, `${s}%`);
  target.style.setProperty(`--bulma-${name}-l`, `${l}%`);
}

/**
 * Apply theme tokens by writing CSS custom properties onto `target`
 * (default: <html>). Bulma 1.x derives its palette from the HSL component vars,
 * so retinting primary/link recolors every Bulma component under `target`.
 * Custom components read the --brand-* vars.
 */
export function applyThemeTokens(tokens: ThemeTokens, target: HTMLElement = document.documentElement): void {
  setScheme(target, 'primary', tokens.brand_primary);
  setScheme(target, 'link', tokens.brand_accent);
  target.style.setProperty('--brand-primary', tokens.brand_primary);
  target.style.setProperty('--brand-accent', tokens.brand_accent);

  const radius = RADIUS_PX[tokens.radius] ?? RADIUS_PX.rounded;
  target.style.setProperty('--bulma-radius', radius);
  target.style.setProperty('--brand-radius', radius);

  const font = FONT_FAMILIES[tokens.font] ?? FONT_FAMILIES.inter;
  target.style.setProperty('--bulma-family-primary', font.stack);
  target.style.setProperty('--brand-font', font.stack);
}

/**
 * Remove all CSS custom properties that applyThemeTokens sets, reverting the
 * element to its inherited (default) values. Call this when the account's
 * whitelabel theme is cleared so the branded look disappears in-session.
 */
export function clearThemeTokens(target: HTMLElement = document.documentElement): void {
  for (const prop of THEME_PROPS) {
    target.style.removeProperty(prop);
  }
}
